import * as React from 'react';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { premiumTheme } from './tokens';

/**
 * KPIStatCard \u2014 Premium metric tile (Stripe-style).
 *
 * Structure:
 *   <label> eyebrow (10px uppercase muted)
 *   <value> bold number (14px \u2013 fits \u00a723.1 cap)
 *   <trend> optional arrow + delta + context
 *
 * Uses `bg-surface-premium-sunken` for subtle depth. Dark-mode adapts via tokens.
 */

type Trend = 'up' | 'down' | 'flat';
type Intent = 'neutral' | 'positive' | 'negative';

export interface KPIStatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Optional trend delta (e.g. "+12%" or "-3") */
  delta?: React.ReactNode;
  /** Direction of trend arrow */
  trend?: Trend;
  /**
   * Semantic color of the trend badge. Overrides automatic mapping from
   * `trend` (useful when "down" is good, e.g. error count).
   */
  intent?: Intent;
  /** Supporting context below the value (e.g. "vs last month") */
  context?: React.ReactNode;
  /** Leading icon (auto-sized 14px) in a soft tile */
  icon?: React.ReactNode;
  /** Surface tone \u2014 `muted` for sunken tile, `card` for full card */
  variant?: 'muted' | 'card';
  /** Make the tile interactive (cursor, hover border) */
  interactive?: boolean;
  /** Show a loading skeleton in the value slot */
  loading?: boolean;
}

const TREND_ICON: Record<Trend, React.ElementType> = {
  up: ArrowUp,
  down: ArrowDown,
  flat: Minus,
};

function resolveIntent(trend: Trend | undefined, intent: Intent | undefined): Intent {
  if (intent) return intent;
  if (trend === 'up') return 'positive';
  if (trend === 'down') return 'negative';
  return 'neutral';
}

const INTENT_CLASS: Record<Intent, string> = {
  positive: 'text-success-strong bg-success-soft',
  negative: 'text-destructive-strong bg-destructive-soft',
  neutral: 'text-muted-foreground bg-surface-premium-sunken',
};

export const KPIStatCard = React.forwardRef<HTMLDivElement, KPIStatCardProps>(
  (
    {
      label,
      value,
      delta,
      trend,
      intent,
      context,
      icon,
      variant = 'muted',
      interactive = false,
      loading = false,
      className,
      ...props
    },
    ref,
  ) => {
    const TrendIcon = trend ? TREND_ICON[trend] : null;
    const resolvedIntent = resolveIntent(trend, intent);

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start gap-3 rounded-xl border border-border-hairline p-4 min-w-0',
          variant === 'muted'
            ? 'bg-surface-premium-sunken'
            : 'bg-surface-premium',
          interactive &&
            'transition-colors duration-150 ease-out hover:border-border-premium cursor-pointer',
          className,
        )}
        {...props}
      >
        {icon && (
          <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-surface-premium text-muted-foreground [&>svg]:h-3.5 [&>svg]:w-3.5">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={premiumTheme.typography.label}>{label}</div>
          <div className="mt-1 flex items-baseline gap-2">
            {loading ? (
              <span
                className="inline-block h-4 w-16 rounded bg-surface-premium-sunken animate-pulse"
                aria-hidden="true"
              />
            ) : (
              <span className="text-[14px] font-semibold text-foreground leading-none tabular-nums">
                {value}
              </span>
            )}
            {delta && !loading && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none',
                  INTENT_CLASS[resolvedIntent],
                )}
              >
                {TrendIcon && <TrendIcon className="h-3 w-3" aria-hidden="true" />}
                {delta}
              </span>
            )}
          </div>
          {context && (
            <div className={cn(premiumTheme.typography.meta, 'mt-1 truncate')}>
              {context}
            </div>
          )}
        </div>
      </div>
    );
  },
);
KPIStatCard.displayName = 'KPIStatCard';

/**
 * KPIStatStrip \u2014 Responsive row wrapper for KPIStatCards.
 */
export const KPIStatStrip = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { columns?: 2 | 3 | 4 | 5 }
>(({ className, columns = 4, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'grid gap-3',
      columns === 2 && 'grid-cols-1 sm:grid-cols-2',
      columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      columns === 4 && 'grid-cols-2 md:grid-cols-4',
      columns === 5 && 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
      className,
    )}
    {...props}
  />
));
KPIStatStrip.displayName = 'KPIStatStrip';
